import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { useWords } from "../lib/workspace";
import { blankAction, defaultConfig, type AutomationsView, type Rule, type Run, type Trigger } from "../lib/automations";
import { describe, RuleEditor, withArticle, type RuleDraft } from "../components/RuleEditor";
import { Empty, ErrorText, Loading, PageHeader } from "../components/ui";

export const useAutomations = (enabled = true) => {
  const { base, orgId } = useOrg();
  return useQuery({ queryKey: [orgId, "automations"], queryFn: () => api<AutomationsView>(`${base}/automations`), enabled });
};

type Words = ReturnType<typeof useWords>;

// Starting points for a new rule, in the company's own words.
function presets(w: Words): { id: string; title: string; text: string; draft: RuleDraft }[] {
  const a = w.lower("appointment");
  return [
    {
      id: "confirm", title: "Booking confirmation", text: `Confirm each new ${a} to the customer.`,
      draft: { name: "Booking confirmation", enabled: true, trigger: "appointment_booked", trigger_config: {}, actions: [
        { ...blankAction("message_customer"), text: `Hi {{customer_first_name}}, your {{service}} at {{business}} is booked for {{when}}. Reply here if you need to change it.`, email_subject: `Booked: {{service}} on {{when}}` }] },
    },
    {
      id: "reminder", title: "Reminder", text: `Remind customers before their ${a}.`,
      draft: { name: "Reminder", enabled: true, trigger: "appointment_before", trigger_config: { minutes: 120 }, actions: [
        { ...blankAction("message_customer"), text: `Hi {{customer_first_name}}, a reminder of your {{service}} at {{business}} today at {{time}}.`, email_subject: `Reminder: {{service}} at {{time}}` }] },
    },
    {
      id: "digest", title: `Today's ${w.lower("appointment", true)} email`, text: "Every morning, email the day's schedule to the people who need it.",
      draft: { name: `Today's ${w.lower("appointment", true)}`, enabled: true, trigger: "daily", trigger_config: defaultConfig("daily"), actions: [
        { ...blankAction("daily_digest"), roles: ["crm_admin", "crm_manager"] }] },
    },
    {
      id: "follow", title: "Follow-up task", text: `After each attended ${a}, give the ${w.lower("resource")} a follow-up task.`,
      draft: { name: "Follow-up task", enabled: true, trigger: "appointment_after", trigger_config: { minutes: 60, only_attended: true }, actions: [
        { ...blankAction("create_task"), title: "Follow up with {{customer_name}} after the {{service}}" }] },
    },
    {
      id: "noshow", title: `Missed ${a}`, text: "Invite people who didn't come to book again.",
      draft: { name: `Missed ${a}`, enabled: true, trigger: "appointment_no_show", trigger_config: {}, actions: [
        { ...blankAction("message_customer"), text: "Hi {{customer_first_name}}, we missed you at {{business}} today. Reply here and we'll find you a new time.", email_subject: "We missed you today" }] },
    },
    {
      id: "lead", title: "New lead alert", text: `Email the team when a new ${w.lower("contact")} arrives from chat or WhatsApp.`,
      draft: { name: "New lead alert", enabled: true, trigger: "contact_created", trigger_config: { sources: ["website_agent", "whatsapp"] }, actions: [
        { ...blankAction("notify_team"), roles: ["crm_manager"], email_subject: "New lead: {{customer_name}}", text: "{{customer_name}} just got in touch. Reply while they're interested." }] },
    },
    {
      id: "won", title: `${w("deal")} won`, text: `Tell managers when ${withArticle(w.lower("deal"))} moves stage.`,
      draft: { name: `${w("deal")} moved`, enabled: true, trigger: "deal_stage_changed", trigger_config: {}, actions: [
        { ...blankAction("notify_team"), assigned: false, roles: ["crm_manager"], email_subject: "{{deal}}: {{stage}}", text: "{{deal}} moved from {{previous_stage}} to {{stage}}." }] },
    },
    {
      id: "blank", title: "Start from scratch", text: "Choose the trigger and actions yourself.",
      draft: { name: "", enabled: true, trigger: "appointment_booked" as Trigger, trigger_config: {}, actions: [blankAction("message_customer")] },
    },
  ];
}

export function Automations() {
  const { base, orgId, role } = useOrg();
  const w = useWords();
  const q = useAutomations();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorText error={q.error} />;
  const v = q.data!;
  const toggle = async (r: Rule) => {
    setError(null);
    try {
      await api(`${base}/automations/${r.id}`, { method: "PATCH", json: { enabled: !r.enabled } });
      await qc.invalidateQueries({ queryKey: [orgId, "automations"] });
    } catch (err) {
      setError(err);
    }
  };
  const sendsEmail = v.rules.some((r) => r.enabled && r.actions.some((a) => a.type === "message_customer" && a.channel !== "whatsapp"));
  return (
    <>
      <PageHeader title="Automations" actions={v.enabled_in_plan && <Link className="button-link" to={`/o/${orgId}/automations/new`}>New automation</Link>} />
      {!v.enabled_in_plan && <p className="notice">Automations aren't part of your plan. Contact SVENEX to add them.</p>}
      <p className="muted">
        Rules that run on their own: confirmations and reminders for customers, alerts and tasks for the team, and the daily schedule email.
        {v.messages_limit != null && <> Automated messages this month: <b>{v.messages_used.toLocaleString()}</b> of {v.messages_limit.toLocaleString()}.</>}
      </p>
      {sendsEmail && !v.customer_reply_email && (
        <p className="notice small">Customer emails show {"your company's name"}, but replies won't reach you until {role === "crm_admin" ? <>you set where they go in <Link to={`/o/${orgId}/settings#customer-messages`}>Settings → Company</Link></> : "an admin sets where they go in Settings → Company"}.</p>
      )}
      <ErrorText error={error} />
      {v.rules.length === 0 ? <Empty>No automations yet.</Empty> : (
        <div className="rule-list">
          {v.rules.map((r) => (
            <div key={r.id} className={`card rule-card${r.enabled ? "" : " off"}`}>
              <div className="inline">
                <label className="switch" title={r.enabled ? "On" : "Off"}>
                  <input type="checkbox" checked={r.enabled} disabled={!v.enabled_in_plan} onChange={() => toggle(r)} aria-label={`${r.name} on`} />
                  <span />
                </label>
                <Link to={`/o/${orgId}/automations/${r.id}`}><b>{r.name}</b></Link>
                {r.source === "system" && <span className="muted small">built in</span>}
                {r.source === "template" && <span className="muted small">from your setup</span>}
                <span className="spacer" />
                <span className="muted small">{r.run_count > 0 ? `Ran ${r.run_count.toLocaleString()} ${r.run_count === 1 ? "time" : "times"}` : "Not run yet"}{r.last_run_at && `, last ${new Date(r.last_run_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`}</span>
              </div>
              <p className="muted small" style={{ margin: ".35rem 0 0" }}>{describe(r, w)}</p>
              {r.last_error && <p className="notice-text small" style={{ margin: ".35rem 0 0" }}>{r.last_error}</p>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** CustomerMessages: where customers' email replies go, and the language for
 *  customers who haven't written yet. Shown in Settings → Company. */
export function CustomerMessages() {
  const { role, allows } = useOrg();
  const q = useAutomations(allows("automations"));
  if (!allows("automations")) return null;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorText error={q.error} />;
  return <ReplySettings v={q.data!} canEdit={role === "crm_admin"} />;
}

function ReplySettings({ v, canEdit }: { v: AutomationsView; canEdit: boolean }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const [email, setEmail] = useState(v.customer_reply_email ?? "");
  const [language, setLanguage] = useState(v.customer_language ?? "en");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  return (
    <section className="card" id="customer-messages">
      <h2>Messages to customers</h2>
      <p className="muted small">Emails to customers come from SVENEX's sending address with your company's name. When a customer replies, it goes to:</p>
      <div className="inline">
        <input type="email" value={email} maxLength={254} disabled={!canEdit} placeholder="bookings@yourcompany.com" onChange={(e) => { setEmail(e.target.value); setSaved(false); }} style={{ maxWidth: 340 }} />
        {canEdit && <button className="secondary" onClick={async () => {
          setError(null);
          try {
            await api(`${base}/automations/settings`, { method: "PUT", json: { customer_reply_email: email.trim() || null } });
            await qc.invalidateQueries({ queryKey: [orgId, "automations"] });
            setSaved(true);
          } catch (err) {
            setError(err);
          }
        }}>Save</button>}
        {saved && <span className="muted small">Saved.</span>}
      </div>
      <h3 style={{ marginBottom: ".25rem" }}>Language for new customers</h3>
      <p className="muted small">Customers get messages in the language they write to you in. Until they've written, use:</p>
      <div className="inline">
        <select value={language} disabled={!canEdit} style={{ maxWidth: 220 }} onChange={async (e) => {
          const next = e.target.value as "ar" | "en";
          setLanguage(next);
          setError(null);
          try {
            await api(`${base}/automations/settings`, { method: "PUT", json: { customer_language: next } });
            await qc.invalidateQueries({ queryKey: [orgId, "automations"] });
          } catch (err) {
            setError(err);
          }
        }}>
          <option value="en">English</option>
          <option value="ar">Arabic (when the rule has an Arabic version)</option>
        </select>
      </div>
      {!canEdit && <p className="muted small">Only admins can change this.</p>}
      <ErrorText error={error} />
    </section>
  );
}

// A fresh editor per rule, so moving between rules never carries a draft over.
export function AutomationPage() {
  const { automationId } = useParams();
  return <AutomationEditor key={automationId ?? "new"} />;
}

function AutomationEditor() {
  const { automationId } = useParams();
  const [params] = useSearchParams();
  const { base, orgId } = useOrg();
  const w = useWords();
  const q = useAutomations();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const creating = !automationId || automationId === "new";
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const preset = params.get("preset");
  const existing = q.data?.rules.find((r) => r.id === automationId);

  useEffect(() => {
    if (!q.data || draft) return;
    if (!creating && existing) {
      setDraft({ name: existing.name, enabled: existing.enabled, trigger: existing.trigger, trigger_config: existing.trigger_config, actions: existing.actions });
    } else if (creating && preset) {
      const p = presets(w).find((x) => x.id === preset);
      if (p) setDraft(structuredClone(p.draft));
    }
  }, [q.data, existing, creating, preset, draft, w]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorText error={q.error} />;
  if (!creating && !existing) return <Empty>This automation doesn't exist.</Empty>;

  if (creating && !draft) {
    return (
      <>
        <PageHeader title="New automation" actions={<Link className="button-link" to={`/o/${orgId}/automations`}>Back</Link>} />
        <p className="muted">Pick a starting point. You can change everything before saving.</p>
        <div className="choice-grid">
          {presets(w).map((p) => (
            <button key={p.id} type="button" className="card choice" onClick={() => setDraft(structuredClone(p.draft))}>
              <b>{p.title}</b>
              <span className="muted small">{p.text}</span>
            </button>
          ))}
        </div>
      </>
    );
  }
  if (!draft) return <Loading />;
  const v = q.data!;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = { name: draft!.name, enabled: draft!.enabled, trigger: draft!.trigger, trigger_config: draft!.trigger_config, actions: draft!.actions };
      if (creating) {
        const r = await api<Rule>(`${base}/automations`, { method: "POST", json: body });
        await qc.invalidateQueries({ queryKey: [orgId, "automations"] });
        navigate(`/o/${orgId}/automations/${r.id}`, { replace: true });
      } else {
        await api(`${base}/automations/${automationId}`, { method: "PATCH", json: body });
        await qc.invalidateQueries({ queryKey: [orgId, "automations"] });
        navigate(`/o/${orgId}/automations`);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title={creating ? "New automation" : draft.name || "Automation"} actions={<Link className="button-link" to={`/o/${orgId}/automations`}>Back</Link>} />
      <section className="card">
        <RuleEditor value={draft} onChange={setDraft} options={v.options} mode="company" words={w}
          canSetExternal={v.can_set_external_recipients} externalLimit={v.external_recipients_limit}
          translate={async (text, kind, trigger, dialect) => (await api<{ text: string }>(`${base}/automations/translate`, { method: "POST", json: { text, kind, trigger, dialect } })).text} />
        <ErrorText error={error} />
        <div className="inline" style={{ marginTop: "1rem" }}>
          <button onClick={save} disabled={busy || !v.enabled_in_plan}>{busy ? "Saving…" : creating ? "Create automation" : "Save"}</button>
          <span className="spacer" />
          {!creating && (
            <button className="link danger-link" onClick={async () => {
              if (!confirm(`Delete "${existing!.name}"? Its history goes with it.`)) return;
              try {
                await api(`${base}/automations/${automationId}`, { method: "DELETE" });
                await qc.invalidateQueries({ queryKey: [orgId, "automations"] });
                navigate(`/o/${orgId}/automations`);
              } catch (err) {
                setError(err);
              }
            }}>Delete</button>
          )}
        </div>
      </section>
      {!creating && <Runs id={automationId!} />}
    </>
  );
}

const runStatus: Record<Run["status"], string> = { queued: "Waiting", done: "Done", skipped: "Skipped", failed: "Failed" };

function Runs({ id }: { id: string }) {
  const { base, orgId } = useOrg();
  const runs = useQuery({ queryKey: [orgId, "automation-runs", id], queryFn: () => api<Run[]>(`${base}/automations/${id}/runs`), refetchInterval: 15_000 });
  return (
    <section className="card">
      <h2>Recent runs</h2>
      {runs.isLoading ? <Loading /> : (runs.data ?? []).length === 0 ? <p className="muted small">It hasn't run yet.</p> : (
        <table className="table compact">
          <thead><tr><th>When</th><th>Customer</th><th>Result</th><th>Messages</th></tr></thead>
          <tbody>
            {runs.data!.map((r) => (
              <tr key={r.id}>
                <td>{new Date(r.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td>{r.contact_id ? <Link to={`/o/${orgId}/contacts/${r.contact_id}`}>{r.contact_name ?? "Customer"}</Link> : "—"}</td>
                <td><span className={`tag run-${r.status}`}>{runStatus[r.status]}</span> <span className="muted small">{r.detail}</span></td>
                <td>{r.messages}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
